import type { DialogResult, DialogRunner } from "./types";

/**
 * Dialog payload posted from the Cognito callback page back to the parent.
 * The /public/auth-dialog-cognito.html page parses ?code= or ?error= from the
 * redirect URL and calls Office.context.ui.messageParent(JSON.stringify(payload)).
 */
export interface CognitoDialogPayload {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

interface OfficeDialog {
  addEventHandler(eventType: string, handler: (arg: { message: string }) => void): void;
  close(): void;
}

/** Result-handler split-out keeps the Office dialog flow shallow. */
function decodeDialogMessage(message: string): DialogResult {
  try {
    const parsed = JSON.parse(message) as CognitoDialogPayload;
    if (parsed.code) {
      return { ok: true, code: parsed.code, state: parsed.state };
    }
    if (parsed.error) {
      return { ok: false, error: parsed.error, description: parsed.error_description };
    }
    return { ok: false, error: "invalid_response", description: "Dialog returned no code or error" };
  } catch {
    return { ok: false, error: "invalid_response", description: "Dialog message was not JSON" };
  }
}

/**
 * Office's Dialog API enforces <AppDomains> only on the INITIAL URL passed to
 * displayDialogAsync. If the manifest registered for the user (personal
 * sideload or Centralized Deployment) is older than what we ship, the Cognito
 * host isn't in the registered AppDomains and the dialog fails with code
 * 12004 ("URL is not trusted"). The proper fix is to update the deployed
 * manifest; this routing-through-launcher is defence-in-depth so a stale
 * manifest doesn't break Connect. Once the dialog is open, in-dialog
 * navigations are unrestricted, so the launcher client-side redirects to the
 * real Cognito URL.
 */
function wrapAuthorizeUrl(authorizeUrl: string, redirectOrigin: string): string {
  return `${redirectOrigin}/auth-dialog-cognito-launch.html?u=${encodeURIComponent(authorizeUrl)}`;
}

function runOfficeDialog(authorizeUrl: string, redirectOrigin: string): Promise<DialogResult> {
  const initialUrl = wrapAuthorizeUrl(authorizeUrl, redirectOrigin);
  return new Promise((resolve) => {
    Office.context.ui.displayDialogAsync(
      initialUrl,
      { height: 60, width: 30, promptBeforeOpen: false },
      (asyncResult) => {
        const result = asyncResult as unknown as {
          status: string;
          value?: unknown;
          error?: { message?: string };
        };
        if (result.status !== "succeeded" || !result.value) {
          resolve({
            ok: false,
            error: "dialog_open_failed",
            description: result.error?.message,
          });
          return;
        }
        const dialog = result.value as OfficeDialog;
        let settled = false;
        dialog.addEventHandler("dialogMessageReceived", (arg) => {
          if (settled) return;
          settled = true;
          const message = decodeDialogMessage(arg.message);
          dialog.close();
          resolve(message);
        });
      },
    );
  });
}

function runPopupDialog(authorizeUrl: string, redirectOrigin: string): Promise<DialogResult> {
  return new Promise((resolve) => {
    const popup = window.open(authorizeUrl, "aiva-auth", "width=480,height=720");
    if (!popup) {
      resolve({ ok: false, error: "popup_blocked" });
      return;
    }
    let settled = false;
    const finish = (result: DialogResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      try {
        popup.close();
      } catch {
        // ignore — popup may already be closed
      }
      resolve(result);
    };
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== redirectOrigin) return;
      if (typeof ev.data !== "string") return;
      finish(decodeDialogMessage(ev.data));
    };
    window.addEventListener("message", onMessage);
  });
}

function isOfficeDialogAvailable(): boolean {
  return (
    typeof Office !== "undefined" &&
    typeof Office.context?.ui?.displayDialogAsync === "function"
  );
}

/** Picks Office Dialog when available, falls back to a window.open popup. */
export function createDialogRunner(): DialogRunner {
  return {
    run(authorizeUrl, redirectOrigin) {
      if (isOfficeDialogAvailable()) {
        return runOfficeDialog(authorizeUrl, redirectOrigin);
      }
      return runPopupDialog(authorizeUrl, redirectOrigin);
    },
  };
}
